/**
 * Seslendirme (v0.1.255): a text voiced through Genesis, one mp3 per part. The script is pasted as it is: a line
 * "## <id>" starts a part (letters, digits, "-" and "_"); without such lines every paragraph is a part (p01, p02, ...).
 * The worker voices a new job within five minutes; the list reads again every 20 seconds while a job is open. Each done
 * part plays here and downloads as an mp3.
 */

import {
  API_BASE_PATH,
  VOICE_JOB_MAX_CHARS,
  VOICE_JOB_MAX_PARTS,
  VOICE_MODELS,
  VOICE_PART_MAX,
  type VoiceJob,
  type VoiceJobBody,
} from '@twin/contracts';
import { useEffect, useMemo, useState } from 'react';
import { useVoice } from '../app/voice.js';
import { formatRelativeClock } from '../lib/format.js';
import { EmptyState } from '../screens/common.js';

const STATUS_TR: Readonly<Record<VoiceJob['status'], string>> = {
  queued: 'sırada',
  running: 'seslendiriliyor',
  done: 'hazır',
  failed: 'bitmedi',
};

const MODEL_TR: Readonly<Record<(typeof VOICE_MODELS)[number], string>> = {
  'bmu-voice': 'Genesis Voice (önerilen)',
  'bmu-voice-fast': 'Genesis Voice Fast (hızlı, ucuz)',
  'bmu-voice-v3': 'Genesis Voice v3 (en doğal)',
};

export interface ParsedPart {
  id: string;
  text: string;
}

/** The parts of a pasted script: "## id" headers, or paragraphs when there are none. */
export function parseScript(script: string): ParsedPart[] {
  const lines = script.replace(/\r\n?/g, '\n').split('\n');
  const headed = lines.some((line) => /^##\s+[A-Za-z0-9][A-Za-z0-9_-]{0,39}\s*$/.test(line));
  if (headed) {
    const parts: ParsedPart[] = [];
    let current: ParsedPart | null = null;
    for (const line of lines) {
      const head = /^##\s+([A-Za-z0-9][A-Za-z0-9_-]{0,39})\s*$/.exec(line);
      if (head?.[1] !== undefined) {
        current = { id: head[1], text: '' };
        parts.push(current);
      } else if (current !== null) {
        current.text = current.text === '' ? line : `${current.text}\n${line}`;
      }
    }
    return parts.map((part) => ({ id: part.id, text: part.text.trim() })).filter((part) => part.text !== '');
  }
  return script
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '')
    .map((text, index) => ({ id: `p${String(index + 1).padStart(2, '0')}`, text }));
}

/** What keeps the script from being sent, in Turkish; null when it can go. */
export function scriptProblem(parts: readonly ParsedPart[]): string | null {
  if (parts.length === 0) return 'Metin boş.';
  if (parts.length > VOICE_JOB_MAX_PARTS) return `En fazla ${VOICE_JOB_MAX_PARTS} parça.`;
  const long = parts.find((part) => part.text.length > VOICE_PART_MAX);
  if (long !== undefined) return `"${long.id}" parçası ${VOICE_PART_MAX} karakterden uzun.`;
  if (new Set(parts.map((part) => part.id)).size !== parts.length) return 'Parça adları tekrar ediyor.';
  const total = parts.reduce((sum, part) => sum + part.text.length, 0);
  if (total > VOICE_JOB_MAX_CHARS) return `Toplam ${VOICE_JOB_MAX_CHARS} karakteri geçiyor.`;
  return null;
}

function usd(micro: number): string {
  return `${(micro / 1_000_000).toFixed(2).replace('.', ',')} $`;
}

function audioUrl(assetId: string): string {
  return `${API_BASE_PATH}/media/voice/${encodeURIComponent(assetId)}`;
}

function JobCard({ job, now }: { job: VoiceJob; now: number }) {
  const done = job.parts.filter((part) => part.status === 'done').length;
  return (
    <article className="voice-job">
      <header className="voice-job__head">
        <strong>{job.title}</strong>
        <span className={`voice-job__status voice-job__status--${job.status}`}>{STATUS_TR[job.status]}</span>
      </header>
      <p className="voice-job__meta">
        {job.voiceName ?? job.voiceId} · {job.model} · hız {job.speed.toFixed(2).replace('.', ',')} · {done}/
        {job.parts.length} parça · {usd(job.costMicroUsd)} · {formatRelativeClock(job.createdAt, now)}
      </p>
      {job.error === null ? null : <p className="voice-job__error">{job.error}</p>}
      <ol className="voice-job__parts">
        {job.parts.map((part) => (
          <li key={part.ord} className="voice-part">
            <span className="voice-part__id mono">{part.id}</span>
            <span className="voice-part__chars">{part.chars} karakter</span>
            {part.assetId === null ? (
              <span className="voice-part__state">
                {part.status === 'failed' ? (part.error ?? 'hata') : 'bekliyor'}
              </span>
            ) : (
              <>
                <audio className="voice-part__audio" controls preload="none" src={audioUrl(part.assetId)}>
                  {/* Narration of the text shown on the page; no separate caption track. */}
                  <track kind="captions" />
                </audio>
                <a className="btn btn--small btn--secondary" href={audioUrl(part.assetId)} download>
                  mp3 indir
                </a>
              </>
            )}
          </li>
        ))}
      </ol>
    </article>
  );
}

export function VoicePage({ now }: { now: number }) {
  const { store, state } = useVoice();
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [model, setModel] = useState<(typeof VOICE_MODELS)[number]>(VOICE_MODELS[0]);
  const [speed, setSpeed] = useState(1);
  const parts = useMemo(() => parseScript(script), [script]);
  const problem = scriptProblem(parts);
  const open = state.view?.jobs.some((job) => job.status === 'queued' || job.status === 'running') === true;

  useEffect(() => {
    void store.reload();
  }, [store]);
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => void store.reload(), 20_000);
    return () => clearInterval(timer);
  }, [open, store]);

  const voices = state.view?.voices ?? [];
  const chosen = voiceId ?? state.view?.defaults.voiceId ?? '';
  const chars = parts.reduce((sum, part) => sum + part.text.length, 0);

  const send = async () => {
    if (problem !== null || title.trim() === '') return;
    const body: VoiceJobBody = {
      title: title.trim(),
      parts: parts.map((part) => ({ id: part.id, text: part.text })),
      model,
      speed,
      ...(chosen === '' ? {} : { voiceId: chosen }),
    };
    if (await store.submit(body)) {
      setTitle('');
      setScript('');
    }
  };

  return (
    <div className="voice">
      <section className="voice-form">
        <label className="field">
          <span className="field__label">Başlık</span>
          <input className="field__input" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">
            Metin · "## parça-adı" satırı yeni parça başlatır; yoksa her paragraf bir parça
          </span>
          <textarea className="field__input" rows={12} value={script} onChange={(e) => setScript(e.target.value)} />
        </label>
        <div className="voice-form__row">
          <label className="field">
            <span className="field__label">Ses</span>
            <select className="field__input" value={chosen} onChange={(e) => setVoiceId(e.target.value)}>
              {voices.length === 0 ? <option value={chosen}>George (varsayılan)</option> : null}
              {voices.map((voice) => (
                <option key={voice.voiceId} value={voice.voiceId}>
                  {voice.name}
                  {voice.description === null ? '' : ` · ${voice.description.slice(0, 60)}`}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Model</span>
            <select
              className="field__input"
              value={model}
              onChange={(e) => setModel(e.target.value as (typeof VOICE_MODELS)[number])}
            >
              {VOICE_MODELS.map((name) => (
                <option key={name} value={name}>
                  {MODEL_TR[name]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">Hız ({speed.toFixed(2).replace('.', ',')})</span>
            <input
              className="field__input"
              type="range"
              min={0.7}
              max={1.2}
              step={0.05}
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
            />
          </label>
        </div>
        <p className="voice-form__summary">
          {parts.length} parça · {chars} karakter{problem === null ? '' : ` · ${problem}`}
        </p>
        {state.sendError === null ? null : <p className="voice-job__error">{state.sendError}</p>}
        <button
          type="button"
          className="btn btn--primary"
          disabled={problem !== null || title.trim() === '' || state.sending}
          onClick={() => void send()}
        >
          {state.sending ? 'Gönderiliyor...' : 'Seslendir'}
        </button>
      </section>
      {state.error === null ? null : <p className="voice-job__error">{state.error}</p>}
      {state.view === null ? null : state.view.jobs.length === 0 ? (
        <EmptyState title="Henüz seslendirme yok" text="Metni yapıştır, sesi seç ve Seslendir'e bas." />
      ) : (
        <div className="voice-jobs">
          {state.view.jobs.map((job) => (
            <JobCard key={job.jobId} job={job} now={now} />
          ))}
        </div>
      )}
    </div>
  );
}
